import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { getUserRole, isPortalUser } from './utils/userRole';
import Layout from './components/Layout';
import Home from './pages/HomePage';
import Login from './pages/Login';
import SelfStudyLessonPage from './pages/SelfStudyLessonPage';

// Helper to handle ChunkLoadErrors when a new version is deployed
const lazyWithRetry = (componentImport) =>
    lazy(async () => {
        const pageHasBeenForceRefreshed = JSON.parse(
            window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
        );

        try {
            const component = await componentImport();
            window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');
            return component;
        } catch (error) {
            if (!pageHasBeenForceRefreshed) {
                // If the error is a ChunkLoadError or MIME type mismatch (text/html instead of JS)
                window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
                return window.location.reload();
            }
            throw error;
        }
    });

// Lazy load heavy routes to create separate JS chunks
const Courses = lazyWithRetry(() => import('./pages/CoursesPage'));
const CourseDetail = lazyWithRetry(() => import('./pages/CourseDetailPage'));
const Prices = lazyWithRetry(() => import('./pages/PricesPage'));
const TeacherDashboard = lazyWithRetry(() => import('./pages/TeacherDashboard'));
const StudentPage = lazyWithRetry(() => import('./pages/StudentPage'));
const ShowGamesPlayerPage = lazyWithRetry(() => import('./pages/ShowGamesPlayerPage'));
const OnlineLessonPage = lazyWithRetry(() => import('./pages/OnlineLessonPage'));
const FullLessonPage = lazyWithRetry(() => import('./pages/FullLessonPage'));
const WordsRepairPage = lazyWithRetry(() => import('./pages/WordsRepairPage'));
const OpenExercisePage = lazyWithRetry(() => import('./pages/OpenExercisePage'));
const WritingCheckerPage = lazyWithRetry(() => import('./pages/WritingCheckerPage'));
const WritingCheckerLandingPage = lazyWithRetry(() => import('./pages/WritingCheckerLandingPage'));
const WritingCheckerTeacherLandingPage = lazyWithRetry(() =>
    import('./pages/WritingCheckerTeacherLandingPage')
);
const PortalPage = lazyWithRetry(() => import('./pages/PortalPage'));
const PortalLoginPage = lazyWithRetry(() => import('./pages/PortalLoginPage'));
const PortalRegisterPage = lazyWithRetry(() => import('./pages/PortalRegisterPage'));
const PortalCheckerPage = lazyWithRetry(() => import('./pages/PortalCheckerPage'));
const PortalChecksPage = lazyWithRetry(() => import('./pages/PortalChecksPage'));
const LegalPage = lazyWithRetry(() => import('./pages/LegalPage'));

// Placeholder for materials component
const Materials = () => <div className="card"><h1>Materials</h1><p>Study resources.</p></div>;

const PageLoader = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '50vh', gap: '16px' }}>
        <div className="animate-spin" style={{ width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTopColor: '#4361ee', borderRadius: '50%' }}></div>
        <div style={{ color: '#64748b', fontWeight: 600 }}>Loading module...</div>
    </div>
);

const ProtectedRoute = ({ children, role }) => {
    const { user } = useAuth();
    const location = useLocation();
    if (!user) {
        return (
            <Navigate
                to="/login"
                replace
                state={{ from: { pathname: location.pathname, search: location.search } }}
            />
        );
    }

    if (isPortalUser(user)) {
        return <Navigate to="/portal" replace />;
    }

    const userRole = getUserRole(user) || 'student';

    if (role && userRole !== role) {
        return <Navigate to="/" />;
    }

    return children;
};

/** Portal-only routes (AI tools, etc.) — requires role portal */
const PortalProtectedRoute = ({ children }) => {
    const { user } = useAuth();
    if (!user) return <Navigate to="/portal/login" replace />;

    const userRole = getUserRole(user);
    if (userRole === 'portal') return children;
    if (userRole === 'teacher') return <Navigate to="/teacher" replace />;
    if (userRole === 'student') return <Navigate to="/student" replace />;
    return <Navigate to="/" replace />;
};

const AppRouter = () => {
    return (
        <Layout>
            <Suspense fallback={<PageLoader />}>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/courses" element={<Courses />} />
                    <Route path="/courses/:courseId" element={<CourseDetail />} />
                    <Route path="/prices" element={<Prices />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/materials" element={<Materials />} />
                    <Route path="/play-games" element={<ShowGamesPlayerPage />} />
                    <Route path="/open/:slug" element={<OpenExercisePage />} />
                    <Route path="/writing-checker" element={<Navigate to="/checker" replace />} />
                    <Route path="/portal/writing-checker" element={<Navigate to="/checker" replace />} />
                    <Route path="/checker" element={<WritingCheckerLandingPage />} />
                    <Route path="/checker_teachers" element={<WritingCheckerTeacherLandingPage />} />
                    <Route path="/checker/teachers" element={<Navigate to="/checker_teachers" replace />} />
                    <Route path="/legal" element={<LegalPage />} />

                    <Route path="/portal" element={<PortalPage />} />
                    <Route path="/portal/login" element={<PortalLoginPage />} />
                    <Route path="/portal/register" element={<PortalRegisterPage />} />
                    <Route path="/portal/checker" element={<PortalCheckerPage />} />
                    <Route
                        path="/portal/checks"
                        element={
                            <PortalProtectedRoute>
                                <PortalChecksPage />
                            </PortalProtectedRoute>
                        }
                    />

                    <Route
                        path="/full-lesson/:id"
                        element={
                            <ProtectedRoute>
                                <FullLessonPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/words-repair"
                        element={
                            <ProtectedRoute role="teacher">
                                <WordsRepairPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/self-study-lesson/:id"
                        element={
                            <ProtectedRoute>
                                <SelfStudyLessonPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/online-lesson/:groupId"
                        element={
                            <ProtectedRoute>
                                <OnlineLessonPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/teacher/*"
                        element={
                            <ProtectedRoute role="teacher">
                                <TeacherDashboard />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/writing-checker"
                        element={
                            <ProtectedRoute role="student">
                                <WritingCheckerPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/student/*"
                        element={
                            <ProtectedRoute role="student">
                                <StudentPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            </Suspense>
        </Layout>
    );
};

export default AppRouter;
